import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Search, ArrowLeft, Sparkles, MessageCircle, FileText, Map, Clock, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useDiscreet } from "@/context/DiscreetContext";
import { AdvisorResult } from "./App";

const KIND = {
  advisor: { label: "Advisor reads", one: "Advisor read", icon: Sparkles, tone: "bg-gold/25 text-cocoa" },
  mentor: { label: "Mentor chats (legacy)", one: "Legacy mentor chat", icon: MessageCircle, tone: "bg-mauve/15 text-mauve" },
  draft: { label: "Drafts", one: "Draft", icon: FileText, tone: "bg-sage/40 text-cocoa" },
  planner: { label: "Plans", one: "Strategy plan", icon: Map, tone: "bg-champagne text-cocoa" },
};
const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const dayKey = (s) => (s ? new Date(s).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "Undated");

export function PinButton({ kind, id, pinned, onChange, size = 16, className = "" }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy) return; setBusy(true);
    try { const r = await api.post("/history/pin", { kind, item_id: id }); onChange?.(r.data.pinned); }
    catch { /* keep state */ } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy} aria-pressed={!!pinned} aria-label={pinned ? "Unpin" : "Pin to favourites"} title={pinned ? "Unpin" : "Pin to favourites"}
      data-testid={`pin-${kind}-${id}`} className={`p-1.5 rounded-full transition-colors hover:bg-champagne/70 disabled:opacity-50 ${pinned ? "text-gold" : "text-cocoa/35 hover:text-cocoa"} ${className}`}>
      <Star size={size} fill={pinned ? "currentColor" : "none"} />
    </button>
  );
}

function Blur({ children, className = "" }) {
  const { discreet } = useDiscreet();
  return <span className={`${discreet ? "discreet-blur" : ""} ${className}`}>{children}</span>;
}

export default function History() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [kind, setKind] = useState(params.get("kind") || "");
  const [profileId, setProfileId] = useState(params.get("profile_id") || "");
  const [pinnedOnly, setPinnedOnly] = useState(params.get("pinned") === "1");
  const setPinned = (id, v) => setData((d) => d ? { ...d, pinned_count: (d.pinned_count || 0) + (v ? 1 : -1), items: d.items.map((i) => (i.id === id ? { ...i, pinned: v } : i)) } : d);
  const [profiles, setProfiles] = useState([]);
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  useEffect(() => { api.get("/profiles").then((r) => setProfiles(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams({ skip: String(skip), limit: String(limit) });
      if (q.trim()) p.set("q", q.trim());
      if (kind) p.set("kind", kind);
      if (profileId) p.set("profile_id", profileId);
      if (pinnedOnly) p.set("pinned_only", "true"); if (pinnedOnly) p.set("pinned", "1");
      setParams(Object.fromEntries([...p].filter(([k]) => !["skip", "limit", "pinned_only"].includes(k))), { replace: true });
      api.get(`/history?${p}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, kind, profileId, pinnedOnly, skip, setParams]);

  const groups = (data?.items || []).reduce((acc, it) => { const k = it.pinned && !pinnedOnly ? "★ Pinned" : dayKey(it.created_at); (acc[k] = acc[k] || []).push(it); return acc; }, {});
  const pages = Math.max(1, Math.ceil((data?.total || 0) / limit));
  const page = Math.floor(skip / limit) + 1;

  return (
    <div data-testid="history-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Your history</p>
      <h1 className="font-heading text-4xl sm:text-5xl text-cocoa">Everything you've asked, and what came back.</h1>
      <p className="mt-2 text-cocoa/70 text-sm max-w-2xl">Every advisor read, draft and plan — plus your archived legacy mentor chats — searchable, and reopenable exactly as you saw it. Private to you.</p>

      <div className="mt-6 ss-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-3.5 text-cocoa/40" />
          <input data-testid="history-search" value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search what you wrote or what SugarSense said…" className="w-full bg-ivory border border-champagne rounded-md h-11 pl-9 pr-3 text-sm text-cocoa outline-none focus:border-mauve" />
        </div>
        {profiles.length > 0 && (
          <select data-testid="history-profile-filter" value={profileId} onChange={(e) => { setSkip(0); setProfileId(e.target.value); }} className="bg-ivory border border-champagne rounded-md h-11 px-3 text-sm text-cocoa">
            <option value="">Everyone</option>{profiles.map((p) => <option key={p.id} value={p.id}>{p.alias}</option>)}
          </select>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="history-kind-filter">
        <button onClick={() => { setSkip(0); setPinnedOnly((v) => !v); }} data-testid="history-pinned-filter" aria-pressed={pinnedOnly} className={`ss-pill h-9 ${pinnedOnly ? "ss-pill-gold" : "ss-pill-ghost"}`}><Star size={13} fill={pinnedOnly ? "currentColor" : "none"} /> Pinned{data ? ` · ${data.pinned_count || 0}` : ""}</button>
        <button onClick={() => { setSkip(0); setKind(""); }} data-testid="history-kind-all" className={`ss-pill h-9 ${!kind ? "ss-pill-primary" : "ss-pill-ghost"}`}>All{data?.counts ? ` · ${Object.values(data.counts).reduce((a, b) => a + b, 0)}` : ""}</button>
        {Object.entries(KIND).map(([k, v]) => (
          <button key={k} onClick={() => { setSkip(0); setKind(k); }} data-testid={`history-kind-${k}`} className={`ss-pill h-9 ${kind === k ? "ss-pill-primary" : "ss-pill-ghost"}`}><v.icon size={13} /> {v.label}{data?.counts ? ` · ${data.counts[k]}` : ""}</button>
        ))}
      </div>

      {!data ? <p className="mt-8 text-sm text-cocoa/50">Loading…</p> : data.items.length === 0 ? (
        <div className="mt-8 ss-card p-8 text-center" data-testid="history-empty">
          <p className="font-heading text-2xl text-cocoa">{pinnedOnly ? "Nothing pinned yet." : q ? "Nothing matches that yet." : "No interactions yet."}</p>
          <p className="mt-2 text-sm text-cocoa/60">{pinnedOnly ? "Tap the star on any read to keep it at the top of your history." : q ? "Try a different word — search covers what you wrote and what came back." : "Your first advisor read will appear here the moment it's done."}</p>
          {!q && <Link to="/ask"><button className="mt-5 ss-pill ss-pill-gold h-11"><Sparkles size={14} /> Ask SugarSense</button></Link>}
        </div>
      ) : (
        <div className="mt-6 space-y-8" data-testid="history-list">
          {Object.entries(groups).map(([day, items]) => (
            <section key={day}>
              <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/45 flex items-center gap-2"><Clock size={11} /> {day}</p>
              <div className="mt-3 space-y-3">
                {items.map((it) => { const K = KIND[it.kind]; const href = it.kind === "advisor" ? `/history/advisor/${it.id}` : it.kind === "mentor" ? `/history/mentor/${it.id}` : `/history/${it.kind}/${it.id}`; return (
                  <Link key={it.id} to={href} data-testid={`history-item-${it.id}`} className="block ss-card p-5 hover:border-mauve/50 transition-colors group">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${K.tone}`}><K.icon size={11} /> {K.one}</span>
                      {it.profile_alias && <span className="px-2 py-0.5 rounded-full bg-champagne/70 text-cocoa/80">{it.profile_alias}</span>}
                      {it.archetype && <span className="px-2 py-0.5 rounded-full bg-mauve/10 text-mauve">{it.archetype}</span>}
                      <span className="ml-auto text-cocoa/45">{fmt(it.created_at)}</span>
                      <PinButton kind={it.kind} id={it.id} pinned={it.pinned} onChange={(v) => setPinned(it.id, v)} size={15} />
                    </div>
                    <p className="mt-2 font-heading text-lg text-cocoa leading-snug group-hover:text-mauve transition-colors"><Blur>{it.title}</Blur></p>
                    {it.summary && <p className="mt-1 text-sm text-cocoa/65 line-clamp-2"><Blur>{it.summary}</Blur></p>}
                  </Link>
                ); })}
              </div>
            </section>
          ))}
          {pages > 1 && (
            <div className="flex items-center justify-between text-xs text-cocoa/60">
              <span>Page {page} of {pages} · {data.total} results</span>
              <div className="flex gap-2">
                <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40" data-testid="history-prev">Prev</button>
                <button disabled={page >= pages} onClick={() => setSkip(skip + limit)} className="ss-pill ss-pill-ghost h-8 disabled:opacity-40" data-testid="history-next">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function HistoryDetail() {
  const { kind, id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const url = kind === "advisor" ? `/history/advisor/${id}` : kind === "mentor" ? `/mentor/sessions/${id}` : `/history?kind=${kind}&limit=100`;
    api.get(url).then((r) => {
      if (kind === "advisor" || kind === "mentor") setD(r.data);
      else { const it = (r.data.items || []).find((x) => x.id === id); it ? setD(it) : setErr("Not found"); }
    }).catch(() => setErr("We couldn't find that interaction."));
  }, [kind, id]);
  const K = KIND[kind] || KIND.advisor;

  return (
    <div data-testid="history-detail-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <button onClick={() => nav(-1)} className="text-sm text-mauve flex items-center gap-1" data-testid="history-back"><ArrowLeft size={14} /> Back to history</button>
      {err && <p className="mt-6 text-rose" data-testid="history-detail-error">{err}</p>}
      {!d && !err && <p className="mt-6 text-sm text-cocoa/50">Loading…</p>}

      {d && kind === "advisor" && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${K.tone}`}><K.icon size={11} /> Advisor read</span>
            {d.profile?.alias && <Link to={`/profiles/${d.profile.id}`} className="px-2 py-0.5 rounded-full bg-champagne/70 text-cocoa/80 hover:text-mauve">{d.profile.alias}</Link>}
            <span className="text-cocoa/45">{fmt(d.session?.created_at)}</span>
            <span className="ml-auto flex items-center gap-1 text-cocoa/60"><PinButton kind="advisor" id={id} pinned={d.pinned} onChange={(v) => setD({ ...d, pinned: v })} /> {d.pinned ? "Pinned" : "Pin"}</span>
          </div>
          <div className="mt-4 ss-card p-6" data-testid="history-detail-input">
            <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/50">What you shared</p>
            <p className="mt-2 text-cocoa leading-relaxed"><Blur>{d.input?.what_happened}</Blur></p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-cocoa/60">
              {d.input?.emotional_state && <span className="px-2 py-0.5 rounded-full bg-champagne/60">Feeling: {d.input.emotional_state}</span>}
              {d.input?.user_goal && <span className="px-2 py-0.5 rounded-full bg-champagne/60">Goal: {d.input.user_goal}</span>}
            </div>
          </div>
          <div className="mt-6" data-testid="history-detail-result"><AdvisorResult data={d.result} /></div>
          {d.feedback && (
            <div className="mt-6 ss-card p-5 text-sm text-cocoa/75" data-testid="history-detail-feedback">
              <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/50">What you told us afterwards</p>
              <p className="mt-2"><Blur>{d.feedback.what_happened || (d.feedback.helpful === true ? "Marked helpful" : d.feedback.helpful === false ? "Marked not helpful" : "Feedback recorded")}</Blur></p>
            </div>
          )}
          <div className="mt-8 flex gap-2">
            <Link to={d.session?.profile_id ? `/ask?profile_id=${d.session.profile_id}` : "/ask"}><button className="ss-pill ss-pill-gold h-11" data-testid="history-detail-followup"><Sparkles size={14} /> Ask a follow-up</button></Link>
          </div>
        </>
      )}

      {d && kind === "mentor" && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${K.tone}`}><K.icon size={11} /> Legacy mentor chat</span>
            <span data-testid="history-mentor-legacy-badge" className="inline-flex items-center px-2 py-0.5 rounded-full bg-champagne/60 text-cocoa/70">Archived — read only. Use Ask SugarSense for new reads.</span>
            <span className="text-cocoa/45">{fmt(d.session?.created_at)}</span>
          </div>
          <h1 className="mt-2 font-heading text-3xl text-cocoa"><Blur>{d.session?.title || "Mentor conversation"}</Blur></h1>
          <div className="mt-6 space-y-3" data-testid="history-detail-messages">
            {(d.messages || []).map((m) => (
              <div key={m.id} className={`max-w-[85%] ss-card p-4 ${m.role === "user" ? "ml-auto bg-champagne/50" : ""}`} data-testid={`mentor-msg-${m.role}`}>
                <p className="text-[10px] uppercase tracking-widest text-cocoa/45">{m.role === "user" ? "You" : "SugarSense"} · {fmt(m.sent_at)}</p>
                <p className="mt-1 text-sm text-cocoa leading-relaxed whitespace-pre-wrap"><Blur>{m.body}</Blur></p>
              </div>
            ))}
          </div>
        </>
      )}

      {d && (kind === "draft" || kind === "planner") && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${K.tone}`}><K.icon size={11} /> {K.one}</span>
            <span className="text-cocoa/45">{fmt(d.created_at)}</span>
          </div>
          <div className="mt-4 space-y-3" data-testid="history-detail-body">
            {Object.entries(d.body || {}).filter(([, v]) => v && (typeof v !== "object" || (Array.isArray(v) && v.length))).map(([k, v]) => (
              <div key={k} className="ss-card p-5">
                <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/50">{k.replace(/_/g, " ")}</p>
                {Array.isArray(v) ? <ul className="mt-2 space-y-1 text-sm text-cocoa">{v.map((x, i) => <li key={i}>· <Blur>{typeof x === "string" ? x : JSON.stringify(x)}</Blur></li>)}</ul>
                  : <p className="mt-2 text-sm text-cocoa leading-relaxed whitespace-pre-wrap"><Blur>{String(v)}</Blur></p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
